import { CheckboxGroup, Flex, Hint, ManagedCheckbox, Spacer } from '@lokalise/louis';
import { useState } from 'react';

import type { EnabledTriggers, IntegrationsConfig, Triggers } from '../../../../types';
import DefaultReviewersCheckbox from './components/DefaultReviewersCheckbox';

type Value = {
    triggers: Triggers;
    defaultBitbucketReviewers: boolean;
};

const triggerKeys: readonly (keyof Triggers)[] = [
    'triggersAmazons3',
    'triggersGcs',
    'triggersGithub',
    'triggersGithubEnterprise',
    'triggersGitlab',
    'triggersBitbucket',
    'triggersBitbucketEnterprise',
    'triggersAzure',
];

export const prepareIncomingValue = (triggers: Triggers): string[] =>
    Object.entries(triggers)
        .filter(([, val]) => val)
        .map(([key]) => key);

export const prepareOutgoingValue = (value: readonly string[], keys: readonly (keyof Triggers)[]): Triggers => {
    const outgoingValue = {} as Triggers;

    keys.forEach((key) => {
        outgoingValue[key] = value.includes(key);
    });

    return outgoingValue;
};

interface TriggersProps {
    enabledTriggers: EnabledTriggers;
    integrationsConfig: IntegrationsConfig;
    value: Value;
    onChange: (value: Value) => void;
}

const TriggersCheckboxes = ({ enabledTriggers, integrationsConfig, value, onChange }: TriggersProps) => {
    const [defaultBitbucketReviewers, setDefaultBitbucketReviewers] = useState<boolean>(
        value.defaultBitbucketReviewers ?? false,
    );

    const [triggers, setTriggers] = useState<Triggers>(value.triggers);

    const onDefaultBitbucketReviewersChange = (val: boolean) => {
        setDefaultBitbucketReviewers(val);
        onChange({
            triggers,
            defaultBitbucketReviewers: val,
        });
    };

    const onTriggersChange = (val: readonly string[]) => {
        const triggersVal = prepareOutgoingValue(val, triggerKeys);
        setTriggers(triggersVal);
        // Uncheck bitbucket default reviewer checkbox when we disable the integration with bitbucket
        if (!triggersVal.triggersBitbucket && defaultBitbucketReviewers) {
            setDefaultBitbucketReviewers(false);
        }
        onChange({
            triggers: triggersVal,
            defaultBitbucketReviewers,
        });
    };

    return (
        <CheckboxGroup
            name="triggers"
            value={prepareIncomingValue(value.triggers)}
            onChange={(_, val) => onTriggersChange(val)}
        >
            <Flex gap={3} direction="column">
                <ManagedCheckbox value="triggersAmazons3" disabled={!enabledTriggers.triggersAmazons3}>
                    {integrationsConfig.amazons3.title}{' '}
                    <Hint placement="top">
                        <div dangerouslySetInnerHTML={{ __html: integrationsConfig.amazons3.description }} />
                    </Hint>
                </ManagedCheckbox>
                <ManagedCheckbox value="triggersGcs" disabled={!enabledTriggers.triggersGcs}>
                    {integrationsConfig.gcs.title}{' '}
                    <Hint placement="top">
                        <div dangerouslySetInnerHTML={{ __html: integrationsConfig.gcs.description }} />
                    </Hint>
                </ManagedCheckbox>
                <ManagedCheckbox value="triggersGithub" disabled={!enabledTriggers.triggersGithub}>
                    {integrationsConfig.gitHub.title}{' '}
                    <Hint placement="top">
                        <div dangerouslySetInnerHTML={{ __html: integrationsConfig.gitHub.description }} />
                    </Hint>
                </ManagedCheckbox>
                <ManagedCheckbox value="triggersGithubEnterprise" disabled={!enabledTriggers.triggersGithubEnterprise}>
                    {integrationsConfig.gitHubEnterprise.title}{' '}
                    <Hint placement="top">
                        <div dangerouslySetInnerHTML={{ __html: integrationsConfig.gitHubEnterprise.description }} />
                    </Hint>
                </ManagedCheckbox>
                <ManagedCheckbox value="triggersGitlab" disabled={!enabledTriggers.triggersGitlab}>
                    {integrationsConfig.gitLab.title}{' '}
                    <Hint placement="top">
                        <div dangerouslySetInnerHTML={{ __html: integrationsConfig.gitLab.description }} />
                    </Hint>
                </ManagedCheckbox>
                <ManagedCheckbox value="triggersBitbucket" disabled={!enabledTriggers.triggersBitbucket}>
                    {integrationsConfig.bitBucket.title}{' '}
                    <Hint placement="top">
                        <div dangerouslySetInnerHTML={{ __html: integrationsConfig.bitBucket.description }} />
                    </Hint>
                    {triggers.triggersBitbucket && (
                        <Spacer left={1} top={1}>
                            <DefaultReviewersCheckbox
                                value={defaultBitbucketReviewers}
                                onChange={onDefaultBitbucketReviewersChange}
                            />
                        </Spacer>
                    )}
                </ManagedCheckbox>
                <ManagedCheckbox
                    value="triggersBitbucketEnterprise"
                    disabled={!enabledTriggers.triggersBitbucketEnterprise}
                >
                    {integrationsConfig.bitBuckEtenterprise.title}{' '}
                    <Hint placement="top">
                        <div dangerouslySetInnerHTML={{ __html: integrationsConfig.bitBuckEtenterprise.description }} />
                    </Hint>
                </ManagedCheckbox>
                <ManagedCheckbox value="triggersAzure" disabled={!enabledTriggers.triggersAzure}>
                    {integrationsConfig.azure.title}{' '}
                    <Hint placement="top">
                        <div dangerouslySetInnerHTML={{ __html: integrationsConfig.azure.description }} />
                    </Hint>
                </ManagedCheckbox>
            </Flex>
        </CheckboxGroup>
    );
};

export default TriggersCheckboxes;
